import { EnvironmentConfig } from '../types/EnvironmentConfig.types'
import { DEFAULT_SEVV_THEME_TOKENS } from '../../../../../../theme/sevvThemeTokenModel'

/**
 * Default Environment Configuration
 * Provides sensible defaults for all configuration sections
 */
export const defaultEnvironmentConfig: EnvironmentConfig = {
  platform: {
    // Picture-in-picture ships off: it is something an admin turns on, not a behaviour every office gets.
    features: { mapEditor: true, chat: true, whiteboard: true, pictureInPicture: false },
    contact: {
      email: 'support@sevv.com',
      phone: '+1 (555) 123-4567',
      address: '123 Business St, Suite 100, City, State 12345',
      supportUrl: 'https://app.sevv.com/support',
    },
    social: {
      twitter: 'https://twitter.com/wigginindustries',
      linkedin: 'https://linkedin.com/company/wigginindustries',
      github: 'https://github.com/wigginindustries',
      facebook: 'https://facebook.com/wigginindustries',
    },
  },
  branding: {
    platformName: 'Sevv',
    platformUrl: 'https://app.sevv.com',
    platformDescription: 'Virtual office platform for remote teams',
    logoUrl: '/logo.png',
    primaryColor: '#f97415',
    secondaryColor: '#3498db',
  },
  designTokens: DEFAULT_SEVV_THEME_TOKENS,
  registration: {
    mode: 'open',
    waitlistUrl: '',
    emailDomains: {
      allowed: ['wiggin.industries', 'example.com'],
      blocked: ['temp-mail.org', '10minutemail.com'],
      requireVerification: true,
    },
    requiredFields: {
      firstName: true,
      lastName: true,
      email: true,
      phone: false,
      company: false,
      title: false,
    },
    verification: {
      emailVerification: true,
      phoneVerification: false,
      adminApproval: false,
      autoApprove: true,
    },
    terms: {
      enabled: true,
      url: 'https://wiggin.industries/terms',
      requireAcceptance: true,
    },
    limits: {
      maxUsers: 1000,
      maxRooms: 100,
      maxStorage: 1024, // 1GB
    },
  },
  security: {
    password: {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSpecialChars: false,
      maxAge: 90, // days
      preventReuse: 5, // last 5 passwords
    },
    session: {
      timeout: 30, // minutes
      maxConcurrent: 3,
      rememberMe: true,
      secureOnly: true,
    },
    rateLimiting: {
      enabled: true,
      requestsPerMinute: 60,
      requestsPerHour: 1000,
    },
    twoFactor: {
      enabled: true,
      required: false,
      methods: ['email', 'totp'],
    },
    ipRestrictions: {
      enabled: false,
      whitelist: [],
      blacklist: [],
    },
    encryption: {
      algorithm: 'AES-256',
      keyRotation: 30, // days
    },
  },
  appearance: {
    theme: {
      mode: 'auto',
    },
    typography: {
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: 'medium',
      fontWeight: 'normal',
    },
    animations: {
      enabled: true,
      duration: 300, // milliseconds
      easing: 'ease-in-out',
    },
    localization: {
      language: 'en',
      timezone: 'UTC',
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12h',
    },
  },
  integrations: {
    analytics: {
      googleAnalytics: {
        enabled: false,
        trackingId: '',
      },
      mixpanel: {
        enabled: false,
        token: '',
      },
      hotjar: {
        enabled: false,
        siteId: '',
      },
    },
    email: {
      provider: 'smtp',
      smtp: {
        host: 'smtp.gmail.com',
        port: 587,
        username: '',
        password: '',
        secure: true,
        senderName: 'Sevv Platform',
        replyTo: 'noreply@wiggin-industries.net',
      },
      mailgun: {
        apiKey: '',
        domain: '',
        senderName: 'Sevv Platform',
        replyTo: 'noreply@wiggin-industries.net',
      },
    },
    oauth: {
      google: {
        enabled: false,
        clientId: '',
        clientSecret: '',
      },
      github: {
        enabled: false,
        clientId: '',
        clientSecret: '',
      },
      microsoft: {
        enabled: false,
        clientId: '',
        clientSecret: '',
      },
    },
    webhooks: {
      enabled: false,
      endpoints: [],
    },
  },
  advanced: {
    performance: {
      caching: {
        enabled: true,
        ttl: 3600, // 1 hour
        maxSize: 100, // MB
      },
      compression: {
        enabled: true,
        level: 6,
      },
      cdn: {
        enabled: false,
        url: '',
      },
    },
    logging: {
      level: 'info',
      file: {
        enabled: true,
        path: '/var/log/wiggin/app.log',
        maxSize: 100, // MB
        maxFiles: 10,
      },
      console: {
        enabled: true,
      },
      external: {
        enabled: false,
        url: '',
        apiKey: '',
      },
    },
    monitoring: {
      healthChecks: {
        enabled: true,
        interval: 60, // seconds
      },
      metrics: {
        enabled: true,
        endpoint: '/metrics',
      },
      alerts: {
        enabled: false,
        email: [],
        webhook: '',
      },
    },
  },
}

/**
 * Minimal Configuration
 * For development or testing environments
 */
export const minimalEnvironmentConfig: EnvironmentConfig = {
  platform: {
    contact: {
      email: 'admin@localhost',
    },
    social: {},
  },
  branding: {
    platformName: 'Sevv',
    platformUrl: 'http://localhost:3000',
    platformDescription: 'Virtual office platform',
    logoUrl: '/logo.png',
    primaryColor: '#f97415',
    secondaryColor: '#3498db',
  },
  designTokens: DEFAULT_SEVV_THEME_TOKENS,
  registration: {
    mode: 'open',
    waitlistUrl: '',
    emailDomains: {
      allowed: [],
      blocked: [],
      requireVerification: false,
    },
    requiredFields: {
      firstName: true,
      lastName: true,
      email: true,
      phone: false,
      company: false,
      title: false,
    },
    verification: {
      emailVerification: false,
      phoneVerification: false,
      adminApproval: false,
      autoApprove: true,
    },
    terms: {
      enabled: false,
      requireAcceptance: false,
    },
    limits: {
      maxUsers: 100,
      maxRooms: 10,
      maxStorage: 100, // 100MB
    },
  },
  security: {
    password: {
      minLength: 6,
      requireUppercase: false,
      requireLowercase: false,
      requireNumbers: false,
      requireSpecialChars: false,
      maxAge: 365, // days
      preventReuse: 0,
    },
    session: {
      timeout: 60, // minutes
      maxConcurrent: 5,
      rememberMe: true,
      secureOnly: false,
    },
    rateLimiting: {
      enabled: false,
      requestsPerMinute: 1000,
      requestsPerHour: 10000,
    },
    twoFactor: {
      enabled: false,
      required: false,
      methods: [],
    },
    ipRestrictions: {
      enabled: false,
      whitelist: [],
      blacklist: [],
    },
    encryption: {
      algorithm: 'AES-256',
      keyRotation: 365, // days
    },
  },
  appearance: {
    theme: {
      mode: 'light',
    },
    typography: {
      fontFamily: 'Inter, sans-serif',
      fontSize: 'medium',
      fontWeight: 'normal',
    },
    animations: {
      enabled: true,
      duration: 300,
      easing: 'ease-in-out',
    },
    localization: {
      language: 'en',
      timezone: 'UTC',
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12h',
    },
  },
  integrations: {
    analytics: {},
    email: {
      provider: 'smtp',
      smtp: {
        host: 'localhost',
        port: 1025,
        username: '',
        password: '',
        secure: false,
        senderName: 'Sevv Platform',
        replyTo: 'noreply@sevv.com',
      },
      mailgun: {
        apiKey: '',
        domain: '',
        senderName: 'Sevv Platform',
        replyTo: 'noreply@sevv.com',
      },
    },
    oauth: {},
    webhooks: {
      enabled: false,
      endpoints: [],
    },
  },
  advanced: {
    performance: {
      caching: {
        enabled: false,
        ttl: 300,
        maxSize: 10,
      },
      compression: {
        enabled: false,
        level: 1,
      },
      cdn: {
        enabled: false,
        url: '',
      },
    },
    logging: {
      level: 'debug',
      file: {
        enabled: false,
        path: '',
        maxSize: 10,
        maxFiles: 3,
      },
      console: {
        enabled: true,
      },
      external: {
        enabled: false,
        url: '',
        apiKey: '',
      },
    },
    monitoring: {
      healthChecks: {
        enabled: false,
        interval: 300,
      },
      metrics: {
        enabled: false,
        endpoint: '',
      },
      alerts: {
        enabled: false,
        email: [],
        webhook: '',
      },
    },
  },
}

/**
 * Get default configuration based on environment
 */
export const getDefaultConfig = (environment: 'development' | 'production' | 'testing' = 'production'): EnvironmentConfig => {
  switch (environment) {
    case 'development':
    case 'testing':
      return minimalEnvironmentConfig
    case 'production':
    default:
      return defaultEnvironmentConfig
  }
}

/**
 * Merge configuration with defaults
 */
export const mergeWithDefaults = (config: Partial<EnvironmentConfig>, environment: 'development' | 'production' | 'testing' = 'production'): EnvironmentConfig => {
  const defaults = getDefaultConfig(environment)
  return deepMerge(defaults, config)
}

/**
 * Deep merge utility function
 */
function deepMerge(target: any, source: any): any {
  const result = { ...target }
  
  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      result[key] = deepMerge(target[key] || {}, source[key])
    } else {
      result[key] = source[key]
    }
  }
  
  return result
} 